import React, { useState } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { useNavigate } from 'react-router-dom';

// store
import { storeSet } from '../../store';

// components
import LogoIcon from '../../icons/LogoIcon';
import FbIcon from '../../icons/FbIcon';
import GoogleIcon from '../../icons/GoogleIcon';
import { useForm } from 'react-hook-form';
import FormField from '../../components/FormField';
import ButtonFormSubmit from '../../components/ButtonWrapper/components/ButtonFormSubmit';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';

const Registration = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const {
        control,
        handleSubmit,
        formState: { isValid },
        watch
    } = useForm({ mode: 'onChange' });
    
    const passwordValue = watch('password');
    
    // Name validation rules
    const nameValidationRules = {
        required: __('Name is required', 'soundbetween'),
        minLength: {
            value: 2,
            message: __('Name must be at least 2 characters long', 'soundbetween')
        },
        pattern: {
            value: /^[a-zA-Z\s]+$/,
            message: __('Name can only contain letters and spaces', 'soundbetween')
        }
    };
    
    // Surname validation rules
    const surnameValidationRules = {
        required: __('Surname is required', 'soundbetween'),
        minLength: {
            value: 2,
            message: __('Surname must be at least 2 characters long', 'soundbetween')
        },
        pattern: {
            value: /^[a-zA-Z\s]+$/,
            message: __('Surname can only contain letters and spaces', 'soundbetween')
        }
    };
    
    // Email validation rules
    const emailValidationRules = {
        required: __('Email is required', 'soundbetween'),
        pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: __('Enter a valid email', 'soundbetween')
        }
    };
    
    // Password validation rules
    const passwordValidationRules = {
        required: __('Password is required', 'soundbetween'),
        minLength: {
            value: 8,
            message: __('Password must be at least 8 characters long', 'soundbetween')
        }
    };
    
    // Confirm password validation rules
    const confirmPasswordValidationRules = {
        required: __('Please confirm your password', 'soundbetween'),
        minLength: {
            value: 8,
            message: __('Password must be at least 8 characters long', 'soundbetween')
        },
        validate: (value) => {
            if (value !== passwordValue) {
                return __('Passwords do not match', 'soundbetween');
            }
            return true;
        }
    };
    
    // Privacy validation rules
    const privacyValidationRules = {
        required: __('You must agree to the Terms of Service and Privacy Policy', 'soundbetween')
    };

    const onSubmit = async ({ name, surname, email, password }) => {
        setLoading(true);

        const newFormData = {
            name, surname, email, password
        };

        try {
            await storeSet('register', newFormData);
            handleSuccessAndDisplay(__('Registered successfully!', 'soundbetween'));
            setTimeout(() => {
                navigate('/login');
            }, [2000])
        } catch (error) {
            // Error already displayed by store action
        } finally {
            setLoading(false);
        }
    };

    const handleGoToLogin= () => {
        navigate('/login');
    }

    return (
        <div className={['appPage', 'appPageLogin'].join(' ')}>
            <div className="appPageLogin__wrapper">

                <LogoIcon/>

                <div className="loginCard">
                    <span className="loginCard__title">{__('Hi!', 'soundbetween')}</span>
                    <span className="loginCard__subttitle">
                        {__('Sign up to start collaborating, creating and sharing', 'soundbetween')}
                    </span>

                    <form className="appForm" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <fieldset>
                            <FormField
                                type="text"
                                name="name"
                                placeholder={__('Name', 'soundbetween')}
                                control={control}
                                rules={nameValidationRules}
                            />

                            <FormField
                                type="text"
                                name="surname"
                                placeholder={__('Surname', 'soundbetween')}
                                control={control}
                                rules={surnameValidationRules}
                            />

                            <FormField
                                type="email"
                                name="email"
                                placeholder={__('Email', 'soundbetween')}
                                control={control}
                                rules={emailValidationRules}
                            />

                            <FormField
                                type="password"
                                name="password"
                                placeholder={__('Password', 'soundbetween')}
                                description={__('The password must contain at least 8  characters', 'soundbetween')}
                                control={control}
                                rules={passwordValidationRules}
                            />

                            <FormField
                                type="password"
                                name="confirmPassword"
                                placeholder={__('Confirm Password', 'soundbetween')}
                                control={control}
                                rules={confirmPasswordValidationRules}
                            />
                        </fieldset>

                        <div className="loginCard__socialDivider">
                            <span className="loginCard__socialDivider-line"></span>
                            <span className="loginCard__socialDivider-label">{__('Or', 'soundbetween')}</span>
                            <span className="loginCard__socialDivider-line"></span>
                        </div>

                        <div className="loginCard__socialButtons">
                            <button type="button" className="loginCard__socialBtn">
                                <FbIcon />
                                <span>{__('Facebook', 'soundbetween')}</span>
                            </button>
                            <button type="button" className="loginCard__socialBtn">
                                <GoogleIcon />
                                <span>{__('Google', 'soundbetween')}</span>
                            </button>
                        </div>

                        <FormField
                            type="checkbox"
                            name="privacy"
                            label={sprintf(
                                __('I\'m agree to %s and %s', 'soundbetween'),
                                `<a href="#">${__('Terms of Service')}</a>`,
                                `<a href="#">${__('Privacy Policy')}</a>`
                            )}
                            control={control}
                            rules={privacyValidationRules}
                        />

                        <ButtonFormSubmit onClick={() => {}} disabled={loading || !isValid}>
                            {__('Confirm', 'soundbetween')}
                        </ButtonFormSubmit>

                        <p className="loginCard__subttitle">
                            {__('Sei già registrato? ', 'soundbetween')}
                            <a href="#" onClick={(e) => {
                                e.preventDefault();
                                handleGoToLogin();
                            }}>
                                {__('Vai alla login', 'soundbetween')}
                            </a>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Registration;